// Pretending the network has fallen over, so somebody can see what this app
// does about it.
//
// Every interesting screen in this app — the ledger's colours, the Alerts tab,
// the push copy, and above all "Your options" — only exists when something has
// gone wrong. On an ordinary Tuesday nothing has, so the work that matters
// most is the work nobody can look at. Waiting for a real trespasser at
// Oakleigh is not a development process.
//
// So: a table of recognisable failures, and a builder that turns one into rows
// in EXACTLY the shape `toRecord` in `src/app-state.js` builds from PTV. Not a
// parallel representation with its own severity and its own colours — the same
// rows, through the same `severityOf`, the same `blockedBy`, the same rule
// matching. A simulation that took a shortcut round any of that would be a
// simulation of the simulation.
//
// THREE RULES, and they are the whole of the design.
//
//   1. PURE. This file reads the bundled network and the clock it is handed,
//      and nothing else. No Vue, no store, no fetch. The reactive half lives in
//      `src/app-state.js`, which is also the only thing that knows a simulation
//      is running.
//   2. NAMES, NOT IDS. A scenario says "Oakleigh" and "Cranbourne", and those
//      are resolved against whichever bundle is in force. A scenario that
//      cannot resolve is not offered — which is what stops Sydney being shown
//      a track fire at South Yarra.
//   3. NEGATIVE `ptvId`s. PTV's ids are positive, so a negative one can never
//      collide with a real notice, and every place in the app that matches
//      rows by `ptvId` — the reconcile, the notify-once ledger, the push
//      receipt — misses these by construction rather than by a guard somebody
//      has to remember to write.
//
// The prose is written the way PTV writes it, deliberately and at some cost to
// how it reads here: `severityOf` and `affectedStops` PARSE these sentences,
// so wording them as a developer would would exercise a code path the real
// feed never takes. Where a sentence looks oddly careful about not naming a
// station, that is why — a whole-line suspension that names one station reads
// as a fault at that station, and the scenario would silently test the wrong
// thing.

import {
  allStations, knownRouteIds, lineName, lineRecord, lineStops,
} from './network.js';
import { normaliseStationName } from './departures.js';
import {
  allModes, lineIdOf, modeOfLine, modeOfRow, routeNumberOf,
} from './modes.js';

/**
 * How long a simulation may run before it turns itself off.
 *
 * An hour. Long enough to work through every screen twice, short enough that
 * somebody who closed the app mid-scenario does not come back on Thursday to a
 * phone insisting the Cranbourne line is suspended. It is not a nicety: the
 * whole feature rests on nobody ever mistaking a simulated suspension for a
 * real one, and a stale simulation is exactly that mistake with a delay on it.
 */
export const SIMULATION_MS = 60 * 60 * 1000;

/** How long ago a simulated disruption started. Recent, but not this second. */
const STARTED_MINUTES_AGO = 6;

/**
 * The base every simulated `ptvId` is counted down from.
 *
 * Negative, and a long way from zero, so that a row can be recognised as
 * simulated from its id alone in a debugger or a log line without anybody
 * having to reach for the `simulated` flag.
 */
export const SIM_ID_BASE = -900_000;

/** The scenario ids, spelled once. Screens and tests refer to these. */
export const SCENARIOS = {
  TRESPASSER: 'trespasser-oakleigh',
  TRACK_FIRE: 'track-fire-south-yarra',
  TUNNEL_STUCK: 'stuck-train-anzac',
  BUSES: 'buses-caulfield-dandenong',
  POINTS: 'points-footscray',
  ILL_PASSENGER: 'ill-passenger-richmond',
  POWER: 'power-flinders-street',
  WEEKEND_WORKS: 'weekend-works-glen-waverley',
  CAR_PARK: 'car-park-nunawading',
  GENERIC_HUB: 'generic-hub',
  GENERIC_LINES: 'generic-lines',
  GENERIC_DELAYS: 'generic-delays',
  // Appended, never inserted: a scenario's position in this list is its
  // `ptvId` band (see `ordinalOf`), and moving one would re-number the rest.
  TRAM_DIVERSION: 'tram-diversion-96',
};

/**
 * Every line in the city, however many that is.
 *
 * A sentinel rather than a list, because the one scenario that wants it —
 * a power fault that stops everything — means "everything", and a list would
 * be seventeen names to keep in step with a bundle a script regenerates.
 */
const ALL_LINES = Symbol('all lines');

// ── The table ────────────────────────────────────────────────────────────────
//
// A scenario is declarative. It names the lines it affects, the stations that
// have to exist for it to make sense, the SCOPE the rows should land in once
// `blockedBy` has read them, and how to word one row for one line.
//
//   scope: 'line'     the whole line is out. No stops on the row, and the prose
//                     names no station — `blockedBy` files it against the route.
//   scope: 'station'  one station is out, and every line through it with it.
//                     The row carries the stop, so both lines through South
//                     Yarra are cut there without either of them being cut
//                     end to end.
//   scope: 'section'  a stretch between two named stations. The row carries the
//                     run of stops, which is what lets the planner route round
//                     the closed half of a line that is otherwise running.

const MELBOURNE = [
  {
    id: SCENARIOS.TRESPASSER,
    name: 'Trespasser at Oakleigh',
    // Named so the scenario is refused in a city without an Oakleigh, even
    // though the rows themselves are whole-line and carry no stop.
    needs: ['Oakleigh'],
    lines: ['Cranbourne', 'Pakenham'],
    scope: 'line',
    type: 'Suspended',
    row: (line) => ({
      title: `Trains suspended, ${line} line`,
      description: `Trains are suspended on the ${line} line while police attend a trespasser `
        + 'on the tracks. Trains may be held at platforms in both directions. Allow extra travel time.',
    }),
  },

  {
    id: SCENARIOS.TRACK_FIRE,
    name: 'Track fire at South Yarra',
    needs: ['South Yarra'],
    // NOT A LIST, and this is the scenario that proves why. It used to be
    // four lines because the bundle used to say Cranbourne and Pakenham called
    // at South Yarra; since the Metro Tunnel opened they dive into the portal
    // before it, and a hand-written list would have gone on naming them —
    // which any Melbourne passenger reads as the app not knowing the network.
    // Asked of the bundle, it is whatever the bundle says today: Frankston and
    // Sandringham, and any V/Line service that comes to call there.
    lines: { through: 'South Yarra' },
    scope: 'station',
    at: 'South Yarra',
    type: 'Suspended',
    row: (line, { at }) => ({
      title: `Trains suspended through ${at}, ${line} line`,
      description: `Emergency services are attending a fire in the rail reservation at ${at}. `
        + `${line} line trains are suspended through ${at} and are not stopping there in either direction.`,
    }),
  },

  {
    id: SCENARIOS.TUNNEL_STUCK,
    name: 'Train stuck at Anzac',
    needs: ['Anzac', 'Town Hall'],
    // The tunnel lines, by way of the tunnel's own station. Nothing else in
    // the bundle goes through Anzac, so this resolves to exactly the pair.
    lines: { through: 'Anzac' },
    scope: 'section',
    from: 'Town Hall',
    to: 'Anzac',
    type: 'Suspended',
    row: (line, { from, to }) => ({
      title: `Major disruption, ${line} line`,
      description: `A disabled train is being removed from the tunnel. Trains are not running `
        + `between ${from} and ${to} in either direction, and ${line} line services are `
        + `terminating and originating at ${from}.`,
    }),
  },

  {
    id: SCENARIOS.BUSES,
    name: 'Buses replace trains, Caulfield to Dandenong',
    needs: ['Caulfield', 'Dandenong'],
    lines: ['Cranbourne', 'Pakenham'],
    scope: 'section',
    from: 'Caulfield',
    to: 'Dandenong',
    // PTV's own word for a stretch of a line rather than the whole of it.
    type: 'Part Suspended',
    row: (line, { from, to }) => ({
      title: `Buses replace trains, ${line} line`,
      description: `Buses replace trains between ${from} and ${to} following a signal fault. `
        + `Buses depart from the bus interchange at ${from} and ${to}. Allow extra travel time.`,
    }),
  },

  {
    id: SCENARIOS.POINTS,
    name: 'Points failure at Footscray',
    needs: ['Footscray'],
    lines: ['Sunbury', 'Werribee', 'Williamstown'],
    scope: 'line',
    type: 'Suspended',
    row: (line) => ({
      title: `Trains suspended, ${line} line`,
      description: `Trains are suspended on the ${line} line following a points failure. `
        + 'Signalling technicians are on site. Allow extra travel time in both directions.',
    }),
  },

  {
    id: SCENARIOS.ILL_PASSENGER,
    name: 'Ill passenger at Richmond',
    needs: ['Richmond'],
    // Every line through Richmond — which in Melbourne is most of the eastern
    // half of the network, and two V/Line lines with it.
    lines: { through: 'Richmond' },
    scope: 'station',
    at: 'Richmond',
    // PTV's own type, so `severityOf` reads DELAYED without consulting the
    // prose. The figure below is what makes it serious enough to reroute.
    type: 'Major Delays',
    row: (line, { at }) => ({
      title: `Major delays, ${line} line`,
      description: `Trains are delayed by up to 25 minutes after an ill passenger was attended `
        + `to at ${at}. Trains are running in both directions and may be held at platforms.`,
    }),
  },

  {
    id: SCENARIOS.POWER,
    name: 'Power fault at Flinders Street',
    needs: ['Flinders Street'],
    lines: ALL_LINES,
    scope: 'line',
    type: 'Suspended',
    row: (line) => ({
      title: `Trains suspended, ${line} line`,
      description: `Trains are suspended on the ${line} line following a major power supply `
        + 'fault in the city. No trains are running into or out of the city in either direction.',
    }),
  },

  // Two of my own, for two paths none of the above reaches.

  {
    id: SCENARIOS.WEEKEND_WORKS,
    name: 'Weekend works, Glen Waverley line',
    needs: ['Burnley', 'Glen Waverley'],
    lines: ['Glen Waverley'],
    scope: 'section',
    from: 'Burnley',
    to: 'Glen Waverley',
    type: 'Planned Works',
    // The one scenario that is NOT current, on purpose. Everything above tests
    // what the app does about trouble; this tests what it does about trouble
    // that has not started yet — a PLANNED row on the ledger, no alert, no
    // reroute, and "Nothing is in the way" on the options screen. Getting that
    // restraint wrong is how a ledger ends up permanently written on.
    status: 'planned',
    startsInHours: 26,
    endsInHours: 74,
    row: (line, { from, to }) => ({
      title: `Buses replace trains, ${line} line`,
      description: `Buses replace trains between ${from} and ${to} from 8.30pm Friday to the last `
        + 'service Sunday for scheduled maintenance works. Buses depart from the bus stop outside each station.',
    }),
  },

  {
    id: SCENARIOS.CAR_PARK,
    name: 'Car park closed at Nunawading',
    needs: ['Nunawading'],
    lines: { through: 'Nunawading' },
    scope: 'station',
    at: 'Nunawading',
    type: 'Car park closure',
    // A NOTICE, and the app is meant to almost ignore it: no ledger colour, no
    // buzz, no reroute — just a line on the line's own screen. This is the
    // scenario that proves the ledger's restraint, which is the judgement the
    // whole app rests on and the one nothing else here exercises.
    row: (line, { at }) => ({
      title: `Car park closure, ${at} station`,
      description: `The commuter car park at ${at} is closed for resurfacing works. `
        + 'Trains are running normally. Additional parking is available on nearby streets.',
    }),
  },
];

/**
 * Melbourne's trams, offered only to a phone that has them on.
 *
 * A tram scenario names ROUTE NUMBERS rather than line names, because that is
 * what a tram route is called, and it names stops the way Yarra Trams does —
 * by number — because that is what `affectedStops` reads. "Stops 3 to 7"
 * is the phrasing a real diversion notice uses, and wording it any other way
 * would exercise a path the real feed never takes.
 */
const MELBOURNE_TRAMS = [
  {
    id: SCENARIOS.TRAM_DIVERSION,
    name: 'Route 96 diverted via Spencer St',
    mode: 'tram',
    routeNumbers: ['96'],
    scope: 'line',
    type: 'Diversion',
    endsInHours: 3,
    row: (line) => ({
      title: `${line}: trams diverted via Spencer St`,
      description: `${line} trams are diverting via Spencer St and Flinders St while Bourke St `
        + 'is closed for an event. Trams are not stopping at Stops 3 to 7. Board at Spencer St '
        + 'or Spring St instead.',
    }),
  },
];

/** The city-specific tables. Everywhere else gets the generic set below. */
const TABLES = { melbourne: MELBOURNE };

/**
 * A mode's own scenarios, by city and then mode id, for a phone with that mode
 * on. A mode with none is simply absent.
 */
const MODE_TABLES = { melbourne: { tram: MELBOURNE_TRAMS } };

/** The scenarios of the modes a phone has on, in the city's mode order. */
const modeScenarios = (cityId, modeIds) => allModes()
  .filter((m) => m.city === String(cityId) && modeIds.includes(m.id))
  .flatMap((m) => MODE_TABLES[m.city]?.[m.id] ?? []);

/** Every mode scenario of every city, for a lookup by id. */
const allModeScenarios = () => Object.values(MODE_TABLES).flatMap((byMode) => Object.values(byMode).flat());

// ── The generic set ──────────────────────────────────────────────────────────
//
// Sydney, Brisbane, Adelaide and Canberra have bundles but no hand-written
// table, and an empty Settings section in four cities out of five is a feature
// that only exists in Melbourne. So three scenarios are DERIVED from whatever
// bundle is in force: its busiest interchange, and its first two lines.
//
// Derived rather than written, because a written one would name a station, and
// a station named for a city whose bundle a script regenerates is a scenario
// that stops resolving one Tuesday without anybody noticing.

/**
 * The station the most lines run through. The city's own idea of a hub.
 *
 * Canberra has ONE line, so there is no interchange at all and the fallback is
 * the city end of it — which is the station anybody there would name. A city
 * with no hub and no lines gets nothing, and is offered nothing.
 */
function busiestInterchange() {
  let best = null;
  for (const station of allStations()) {
    const count = Array.isArray(station.lines) ? station.lines.length : 0;
    if (count < 2) continue;
    // Ties broken by name so the answer is stable across bundle regenerations
    // rather than depending on the order rows happen to be written in.
    if (!best || count > best.count
      || (count === best.count && String(station.name) < String(best.station.name))) {
      best = { station, count };
    }
  }
  if (best) return best.station;
  const [first] = knownRouteIds().sort((a, b) => a - b);
  return Number.isFinite(first) ? lineStops(first)[0] ?? null : null;
}

/**
 * What runs on this line, as the word a notice would use.
 *
 * Adelaide and Canberra have tram lines in their bundles, and a simulated
 * notice saying TRAINS ARE SUSPENDED on the Canberra light rail is the kind of
 * small wrongness that makes somebody stop believing the rest of the screen.
 */
function vehicle(routeId, capital = false) {
  // A mode with stops of its own says its own word; the light rail lines that
  // sit in a train bundle are judged and worded as trains, as they always were.
  const mode = modeOfRow({ routeId, mode: lineRecord(routeId)?.mode });
  const word = mode ? mode.labels.many : 'trains';
  return capital ? word[0].toUpperCase() + word.slice(1) : word;
}

function genericScenarios() {
  const hub = busiestInterchange();
  const lines = knownRouteIds().sort((a, b) => a - b).map((id) => lineName(id)).filter(Boolean);
  const out = [];

  if (hub) {
    out.push({
      id: SCENARIOS.GENERIC_HUB,
      name: `Signal fault at ${hub.name}`,
      needs: [hub.name],
      lines: { through: hub.name },
      scope: 'station',
      at: hub.name,
      type: 'Suspended',
      row: (line, { at, routeId }) => ({
        title: `${vehicle(routeId, true)} suspended through ${at}, ${line} line`,
        description: `A signalling fault is being attended to at ${at}. ${line} line `
          + `${vehicle(routeId)} are suspended through ${at} and are not stopping there in `
          + 'either direction.',
      }),
    });

    out.push({
      id: SCENARIOS.GENERIC_DELAYS,
      name: `Major delays at ${hub.name}`,
      needs: [hub.name],
      lines: { through: hub.name },
      scope: 'station',
      at: hub.name,
      type: 'Major Delays',
      row: (line, { at, routeId }) => ({
        title: `Major delays, ${line} line`,
        description: `${vehicle(routeId, true)} are delayed by up to 25 minutes after an earlier `
          + `equipment fault at ${at}. ${vehicle(routeId, true)} are running in both directions `
          + 'and may be held at platforms.',
      }),
    });
  }

  // Two lines where the city has two, one where it has one. Canberra has one.
  const pair = lines.slice(0, 2);
  if (pair.length) {
    out.push({
      id: SCENARIOS.GENERIC_LINES,
      name: pair.length === 2
        ? `Suspended, ${pair[0]} and ${pair[1]}`
        : `Suspended, ${pair[0]}`,
      needs: [],
      lines: pair,
      scope: 'line',
      type: 'Suspended',
      row: (line, { routeId }) => ({
        title: `${vehicle(routeId, true)} suspended, ${line} line`,
        description: `${vehicle(routeId, true)} are suspended on the ${line} line following an `
          + 'equipment fault. Allow extra travel time in both directions.',
      }),
    });
  }

  return out;
}

// ── Resolution ───────────────────────────────────────────────────────────────

const sameName = (a, b) => normaliseStationName(a) === normaliseStationName(b);

/** One station of the bundle in force, by name, or null. */
function stationNamed(name) {
  if (!name) return null;
  return allStations().find((s) => sameName(s.name, name)) ?? null;
}

/** One route id of the bundle in force, by line name, or null. */
function routeNamed(name) {
  if (!name) return null;
  const want = String(name).trim().toLowerCase();
  return knownRouteIds().find((id) => String(lineName(id) ?? '').trim().toLowerCase() === want) ?? null;
}

/** Every route the bundle says runs through this station, in id order. */
function routesThrough(name) {
  const station = stationNamed(name);
  if (!station) return [];
  return knownRouteIds()
    .filter((id) => lineStops(id).some((s) => s.stopId === station.stopId))
    .sort((a, b) => a - b);
}

/**
 * The route ids a scenario is about, or null where it cannot be resolved here.
 *
 * Null rather than an empty list, because the two mean different things: an
 * empty list is a scenario that resolved to nothing, and null is a scenario
 * this city has no business being offered.
 */
function routesFor(scenario) {
  if (scenario.routeNumbers) {
    const mode = [...allModes()].find((m) => m.id === scenario.mode && m.band);
    const ids = scenario.routeNumbers.map((n) => lineIdOf(mode, n));
    return ids.length && ids.every((id) => id !== null && modeOfLine(id) === mode) ? ids : null;
  }
  if (scenario.lines === ALL_LINES) {
    const all = knownRouteIds().sort((a, b) => a - b);
    return all.length ? all : null;
  }
  if (scenario.lines && typeof scenario.lines === 'object' && scenario.lines.through) {
    const through = routesThrough(scenario.lines.through);
    return through.length ? through : null;
  }
  const ids = (scenario.lines ?? []).map(routeNamed);
  // ALL of them, not the ones that happened to resolve. A "Cranbourne and
  // Pakenham" scenario that quietly became a Pakenham-only scenario is a
  // scenario that no longer means what its name says.
  return ids.every((id) => Number.isFinite(id)) && ids.length ? ids : null;
}

/** Does every station this scenario names exist in the bundle in force? */
function stationsResolve(scenario) {
  const named = [
    ...(scenario.needs ?? []),
    scenario.at, scenario.from, scenario.to,
  ].filter(Boolean);
  return named.every((name) => stationNamed(name) !== null);
}

/** Can this scenario be offered at all, here, now? */
export function resolves(scenario) {
  if (!scenario) return false;
  if (!stationsResolve(scenario)) return false;
  const routes = routesFor(scenario);
  if (!routes) return false;
  // A section needs both ends ON the line, in order — a bundle where one of
  // them is on a different branch would produce a row claiming a stretch that
  // does not exist.
  if (scenario.scope === 'section') {
    return routes.every((routeId) => sectionStops(routeId, scenario.from, scenario.to).length >= 2);
  }
  if (scenario.scope === 'station') {
    return routes.length > 0;
  }
  return true;
}

/** The run of stops from one named station to another, inclusive, on one line. */
function sectionStops(routeId, fromName, toName) {
  const stops = lineStops(routeId);
  const a = stops.findIndex((s) => sameName(s.name, fromName));
  const b = stops.findIndex((s) => sameName(s.name, toName));
  if (a < 0 || b < 0) return [];
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return stops.slice(lo, hi + 1);
}

/**
 * Every scenario this city can actually be shown.
 *
 * The city id selects the table; the BUNDLE in force is what each scenario is
 * resolved against, which is the same thing in the app — `src/network.js`
 * answers for `currentCity()` — and is worth stating because a test that sets
 * one and asks for the other will get an honest answer to the wrong question.
 *
 * The generic set is appended only where the city has no table of its own, so
 * Melbourne is not offered "Signal fault at Flinders Street" beside the seven
 * scenarios that were written for it.
 */
export function scenariosFor(cityId, { modes = [] } = {}) {
  const table = TABLES[String(cityId)] ?? [];
  const offered = table.filter(resolves);
  // Each mode's own, after the trains', and only where it is on: a train-only
  // phone's list of scenarios is the list it always was.
  const modeOffered = modeScenarios(cityId, modes).filter(resolves);
  if (offered.length) return [...offered, ...modeOffered].map(summary);
  return [...genericScenarios().filter(resolves), ...modeOffered].map(summary);
}

/** What a screen needs to draw a row for a scenario. */
function summary(scenario) {
  return { id: scenario.id, name: scenario.name, scope: scenario.scope };
}

/** One scenario by id, from whichever table holds it, or null. */
export function scenarioById(id, cityId = null) {
  const wanted = String(id ?? '');
  const tables = cityId === null
    ? [...Object.values(TABLES).flat(), ...allModeScenarios(), ...genericScenarios()]
    : [...(TABLES[String(cityId)] ?? []), ...Object.values(MODE_TABLES[String(cityId)] ?? {}).flat(), ...genericScenarios()];
  return tables.find((s) => s.id === wanted) ?? null;
}

// ── The rows ─────────────────────────────────────────────────────────────────

/**
 * The rows a scenario produces, in the shape `toRecord` builds from PTV.
 *
 * Every field `toRecord` writes is written here, including the ones that are
 * always null, because the point of the exercise is that no screen can tell
 * the difference. The two fields that are NOT PTV's are `simulated`, which is
 * how `app-state.js` finds these rows again to take them away, and `id`, which
 * is a string where a stored row's is a number — the detail screen matches on
 * it, and a string can never collide with an id the store allocated.
 *
 * `now` is handed in rather than read, so a test can pin the clock and so the
 * rows a reload rebuilds are identical to the ones it lost.
 */
export function rowsFor(scenario, { now = Date.now() } = {}) {
  const spec = typeof scenario === 'string' ? scenarioById(scenario) : scenario;
  if (!spec || !resolves(spec)) return [];

  const routes = routesFor(spec) ?? [];
  const at = spec.at ? stationNamed(spec.at) : null;
  const hour = 60 * 60 * 1000;

  const status = spec.status ?? 'current';
  const startedAt = new Date(
    Number.isFinite(spec.startsInHours)
      ? now + spec.startsInHours * hour
      : now - STARTED_MINUTES_AGO * 60 * 1000,
  ).toISOString();
  const endsAt = Number.isFinite(spec.endsInHours)
    ? new Date(now + spec.endsInHours * hour).toISOString()
    : null;
  const stamp = new Date(now).toISOString();

  return routes.map((routeId, i) => {
    const owner = modeOfLine(routeId);
    const name = owner
      ? `${owner.labels.line.charAt(0).toUpperCase()}${owner.labels.line.slice(1)} ${routeNumberOf(owner, routeId)}`
      : (lineName(routeId) ?? `Line ${routeId}`);
    const { title, description } = spec.row(name, {
      routeId, at: at?.name ?? '', from: spec.from ?? '', to: spec.to ?? '',
    });

    const stops = stopsFor(spec, routeId, at);
    const ptvId = SIM_ID_BASE - (ordinalOf(spec.id) * 100) - i;

    return {
      id: `sim:${ptvId}`,
      ptvId,
      title,
      description,
      line: routeId,
      status,
      type: spec.type ?? '',
      startedAt,
      endsAt,
      stops,
      stopScoped: stops.length > 0,
      // Deliberately null. A simulated notice must not link to PTV's real live
      // travel updates page: the one place somebody could check whether this
      // is true is the one place this row must not send them, because it would
      // show them a network that is running perfectly and make the app look
      // broken rather than honest.
      url: null,
      // Stamped so the Alerts tab shows them — that tab lists what was
      // announced, and a simulation nobody can see on it is half a simulation.
      // These stamps live on an in-memory row and are never written back
      // through `saveDisruption`, so the real ledger's notify-once record is
      // untouched.
      seenAt: stamp,
      sentAt: startedAt,
      clearedAt: null,
      updatedAt: stamp,
      publishedAt: startedAt,
      delayMinutes: null,
      /** The flag every reader of these rows keys off. */
      simulated: true,
    };
  });
}

/** The stops a row carries, which is what decides its scope in `blockedBy`. */
function stopsFor(spec, routeId, at) {
  if (spec.scope === 'station') {
    return at ? [{ stopId: at.stopId, name: at.name }] : [];
  }
  if (spec.scope === 'section') {
    return sectionStops(routeId, spec.from, spec.to).map((s) => ({ stopId: s.stopId, name: s.name }));
  }
  // Whole line. No stops at all, and the prose names none either — see the
  // note at the top of this file.
  return [];
}

/**
 * A stable number per scenario, so two scenarios cannot mint the same `ptvId`.
 *
 * Its position in `SCENARIOS`, which is a closed list spelled once — a hash
 * would be shorter and would collide one day, silently, in the one field whose
 * whole job is never to collide.
 */
const ORDINALS = Object.values(SCENARIOS);

function ordinalOf(id) {
  const i = ORDINALS.indexOf(String(id));
  return i < 0 ? ORDINALS.length : i;
}

/** Is this row one of ours? The one question every other file asks. */
export function isSimulated(row) {
  return row?.simulated === true || Number(row?.ptvId) <= SIM_ID_BASE;
}

/** Has a simulation started at this instant run its hour out? */
export function simulationExpired(startedAt, now = Date.now()) {
  const started = Date.parse(startedAt ?? '');
  if (!Number.isFinite(started)) return true;
  return now - started >= SIMULATION_MS;
}
